import { createColumnHelper, type CellContext } from '@tanstack/react-table';
import { MoreHorizontalIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { AccountDTO, MembershipDTO } from '@invenco/common-interface/accounts';
import { UserAction } from '@invenco/common-interface/accounts';
import { AccessContext } from '@invenco/common-interface/auth';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useGatewayMutation } from '@/shared/hooks/queries';
import { useCurrentUserQuery } from '@/shared/hooks/queries/common';

import { InviteUserDialogContent } from './invite-user-dialog-content';

type Props = {
  account: Partial<AccountDTO>;
  isLoading?: boolean;
  isNew?: boolean;
};

type MembershipRow = MembershipDTO;

const columnHelper = createColumnHelper<MembershipRow>();

function UserActions({
  membership,
  isNew,
  onResendInvite,
  onRemove,
}: {
  membership: MembershipRow;
  isNew?: boolean;
  onResendInvite: (userId: string) => Promise<Result>;
  onRemove: (membershipId: string) => Promise<Result>;
}) {
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);

  if (isNew) return null;

  const membershipId = membership.id;
  const userId = membership.user?.id ?? membership.userId;
  const canResend = membership.user?.allowableActions?.includes(UserAction.RESEND_INVITE);
  const hasRemove = Boolean(membershipId);

  if (!canResend && !hasRemove) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Open user actions">
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canResend && userId ? (
            <DropdownMenuItem onSelect={() => void onResendInvite(userId)}>
              Resend invite
            </DropdownMenuItem>
          ) : null}
          {hasRemove ? (
            <DropdownMenuItem variant="destructive" onSelect={() => setIsRemoveOpen(true)}>
              Remove user
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {hasRemove && membershipId ? (
        <AlertDialogWithAction
          open={isRemoveOpen}
          onOpenChange={setIsRemoveOpen}
          onAction={() => onRemove(membershipId)}
        >
          <AlertDialogWithActionContent>
            <AlertDialogWithActionHeader>
              <AlertDialogWithActionTitle>Remove user?</AlertDialogWithActionTitle>
              <AlertDialogWithActionDescription>
                This removes access for{' '}
                {membership.userEmail ?? membership.user?.email ?? 'this user'}.
              </AlertDialogWithActionDescription>
            </AlertDialogWithActionHeader>
            <AlertDialogWithActionFooter>
              <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
              <AlertDialogWithActionSubmit>Remove</AlertDialogWithActionSubmit>
            </AlertDialogWithActionFooter>
          </AlertDialogWithActionContent>
        </AlertDialogWithAction>
      ) : null}
    </>
  );
}

const createUserActionsCell = (
  isNew: boolean | undefined,
  onResendInvite: (userId: string) => Promise<Result>,
  onRemove: (membershipId: string) => Promise<Result>,
) =>
  function UserActionsCell({ row }: CellContext<MembershipRow, unknown>) {
    return (
      <UserActions
        membership={row.original}
        isNew={isNew}
        onResendInvite={onResendInvite}
        onRemove={onRemove}
      />
    );
  };

export function UsersCard({ account, isLoading, isNew }: Props) {
  const { accountsGateway } = useGateways();
  const accountId = account.id;
  const { data: currentUser, refetch: refetchUser } = useCurrentUserQuery();
  const memberships = account.memberships ?? [];

  const { mutate: addUser } = useGatewayMutation({
    mutationFn: (membership: Partial<MembershipDTO>) =>
      accountsGateway.createMembership(accountId!, {
        ...membership,
        accessContext: AccessContext.SELLER,
      }),
    onSuccess: ({ userId }) => {
      if (currentUser?.id === userId) {
        void refetchUser();
      }
    },
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
    successMessage: 'Added user to account',
  });

  const { mutate: deleteMembership } = useGatewayMutation({
    mutationFn: (membershipId: string) =>
      accountsGateway.deleteMembership(accountId!, membershipId),
    onSuccess: (_, membershipId) => {
      if (currentUser?.memberships?.some((membership) => membership.id === membershipId)) {
        void refetchUser();
      }
    },
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
    successMessage: 'Removed user from account',
  });

  const { mutate: resendInvite } = useGatewayMutation({
    mutationFn: (userId: string) => accountsGateway.resendInvite(userId),
    linkedQuery: ['accounts', accountId],
    linkResponseToQuery: false,
    successMessage: 'Resent invitation email',
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('userName', {
        header: 'Name',
      }),
      columnHelper.accessor('userEmail', {
        header: 'Email',
      }),
      columnHelper.display({
        id: 'actions',
        cell: createUserActionsCell(isNew, resendInvite, deleteMembership),
        meta: { align: 'right' },
      }),
    ],
    [deleteMembership, isNew, resendInvite],
  );

  const { state, onSortingChange } = useDataTableState({ columns, tableId: 'account-users' });

  const table = useDataTable({
    data: memberships,
    columns,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
  });

  return (
    <Card aria-label="Users">
      <CardHeader>
        <DataTableCardTitle table={table}>Users</DataTableCardTitle>
        <CardAction>
          <FormDialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={isNew || !accountId}>
                Invite user
              </Button>
            </DialogTrigger>
            <InviteUserDialogContent
              onSubmit={async (data) => {
                if (!accountId) return Result.fail('Account ID missing');
                return addUser(data);
              }}
            />
          </FormDialog>
        </CardAction>
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
